import { makeAutoObservable, runInAction } from "mobx";
import api from "../../../../services/api";

class AdminStore {
  // Пользователи с ролями и правами
  users = [];
  loadingUsers = false;

  // Роли и права
  roles = [];
  permissions = [];
  loadingRoles = false;
  loadingPermissions = false;

  error = null;
  selectedTab = 'users';

  constructor() {
    makeAutoObservable(this);

  }

  // ===== Управление пользователями =====

  // Запрашивает пользователей с их ролями и правами (ожидается формат: [{ id, name, roles: [...] }, ...])
  async fetchUsersWithRolesAndPermissions() {
    this.loadingUsers = true;
    try {
      const response = await api.get('/roles/users/withRolesAndPermissions');
      console.log("Ответ API (users):", response.data);
      const users = response.data || [];
      runInAction(() => {
        this.users = users;
        this.error = null;
      });
    } catch (err) {
      runInAction(() => {
        this.error = err.message || "Ошибка при загрузке пользователей с ролями и правами";
      });
    } finally {
      runInAction(() => {
        this.loadingUsers = false;
      });
    }
  }
  

  // Алиас для совместимости
  fetchUsers() {
    return this.fetchUsersWithRolesAndPermissions();
  }

  // Создаёт нового пользователя.
  // Ожидается, что для создания используется POST /users, и сервер возвращает созданного пользователя.


  async createUser(userData) {
    try {
      const response = await api.post('/users', userData);
      runInAction(() => {
        this.users.push(response.data);
        this.error = null;
      });
    } catch (err) {
      runInAction(() => {
        this.error = err.message || "Ошибка при создании пользователя";
      });
      throw err;
    }
  }

  async updateUser(userId, userData) {
    try {
      const response = await api.patch(`/users/${userId}`, userData);
      runInAction(() => {
        this.users = this.users.map(user =>
          user.id === userId ? response.data : user
        );
        this.error = null;
      });
    } catch (err) {
      runInAction(() => {
        this.error = err.message || "Ошибка при обновлении пользователя";
      });
      throw err;
    }
  }


  async setUserRole(userId, roleId) {
    try {
      if (roleId != null) {
        // Назначаем роль
        await api.post(`/roles/users/${userId}/roles`, { roleId });
      } else {
        // Снимаем любую роль
        await api.delete(`/users/${userId}/roles`);
      }

      runInAction(() => {
        // Обновляем локальную модель пользователя
        const user = this.users.find(u => u.id === userId);
        if (!user) return;

        if (roleId != null) {
          // Назначение: добавляем роль в массив
          const role = this.roles.find(r => r.id === roleId);
          if (!role) return;
          if (!Array.isArray(user.roles)) user.roles = [];
          if (!user.roles.some(r => r.id === roleId)) {
            user.roles.push(role);
          }
        } else {
          // Снятие: очищаем массив ролей
          user.roles = [];
        }

        this.error = null;
      });
    } catch (err) {
      runInAction(() => {
        this.error = err.message || (roleId != null
          ? 'Ошибка при назначении роли'
          : 'Ошибка при снятии роли');
      });
      throw err;
    }
  }


  async deleteUser(userId) {
    try {
      await api.delete(`/users/${userId}`);
      runInAction(() => {
        this.users = this.users.filter(user => user.id !== userId);
      });
    } catch (err) {
      runInAction(() => {
        this.error = err.message || "Ошибка при удалении пользователя";
      });
      throw err;
    }
  }

  // Смена пароля пользователю (админом)
  async changeUserPassword(userId, newPassword) {
    try {
      // раньше было { password: newPassword }
      await api.patch(`/users/${userId}/password`, { newPassword });
      runInAction(() => {
        this.error = null;
      });
    } catch (err) {
      runInAction(() => {
        this.error = err.message || "Ошибка при смене пароля";
      });
      throw err;
    }
  }

  // ===== Объединённая загрузка ролей и прав =====

  async fetchRolesAndPermissions() {
    this.loadingRoles = true;
    this.loadingPermissions = true;
    try {
      // Единый запрос для получения ролей и прав.
      // Ожидается, что ответ имеет структуру: { roles: [...], permissions: [...] }
      const response = await api.get('/roles/withAllPermissions');
      const { roles, permissions } = response.data;
      runInAction(() => {
        this.roles = roles || [];
        this.permissions = permissions || [];
        this.error = null;
      });
    } catch (err) {
      runInAction(() => {
        this.error = err.message || "Ошибка при загрузке ролей и прав";
      });
    } finally {
      runInAction(() => {
        this.loadingRoles = false;
        this.loadingPermissions = false;
      });
    }
  }

  // ===== Работа с ролями =====

  setSelectedTab(tab) {
    this.selectedTab = tab;
  }

  async createRole(name) {
    try {
      const response = await api.post('/roles/roles', { name });
      runInAction(() => {
        this.roles.push(response.data);
        this.error = null;
      });
    } catch (err) {
      runInAction(() => {
        this.error = err.message || "Ошибка при создании роли";
      });
    }
  }

  async deleteRole(roleId) {
    try {
      await api.delete(`/roles/roles/${roleId}`);
      runInAction(() => {
        this.roles = this.roles.filter(role => role.id !== roleId);
      });
    } catch (err) {
      runInAction(() => {
        this.error = err.message || "Ошибка при удалении роли";
      });
    }
  }

  // ===== Работа с правами =====

  async createPermission(permissionData) {
    try {
      const response = await api.post('/roles/permissions', permissionData);
      runInAction(() => {
        this.permissions.push(response.data);
        this.error = null;
      });
    } catch (err) {
      runInAction(() => {
        this.error = err.message || "Ошибка при создании права";
      });
    }
  }

  async deletePermission(permissionId) {
    try {
      await api.delete(`/roles/permissions/${permissionId}`);
      runInAction(() => {
        this.permissions = this.permissions.filter(p => p.id !== permissionId);
      });
    } catch (err) {
      runInAction(() => {
        this.error = err.message || "Ошибка при удалении права";
      });
    }
  }

  // ===== Дополнительные методы для назначения прав =====

  // Возвращает назначенные и доступные права для выбранной роли.
  // Предполагается, что у каждой роли есть поле permissions с назначенными правами.
  updateRolePermissions(selectedRoleId) {
    const role = this.roles.find(r => r.id === selectedRoleId);
    if (!role) return { assigned: [], available: [] };
    const assigned = role.permissions || [];
    const available = this.permissions.filter(permission =>
      !assigned.some(ap => ap.id === permission.id)
    );
    return { assigned, available };
  }

  async addPermissionToRole(roleId, permissionId) {
    try {
      await api.post(`/roles/roles/${roleId}/permissions/${permissionId}`);
      runInAction(() => {
        const role = this.roles.find(r => r.id === roleId);
        const permission = this.permissions.find(p => p.id === permissionId);
        if (role && permission) {
          if (!role.permissions) role.permissions = [];
          if (!role.permissions.find(p => p.id === permissionId)) {
            role.permissions.push(permission);
          }
        }
      });
    } catch (err) {
      runInAction(() => {
        this.error = err.message || "Ошибка при добавлении права к роли";
      });
      throw err;
    }
  }

  async removePermissionFromRole(roleId, permissionId) {
    try {
      await api.delete(`/roles/roles/${roleId}/permissions/${permissionId}`);
      runInAction(() => {
        const role = this.roles.find(r => r.id === roleId);
        if (role && role.permissions) {
          role.permissions = role.permissions.filter(p => p.id !== permissionId);
        }
      });
    } catch (err) {
      runInAction(() => {
        this.error = err.message || "Ошибка при удалении права у роли";
      });
      throw err;
    }
  }

    // Флаг загрузки прав
    loadingPermissions = false;
    // Массив прав
    permissions = [];
  
    // ===== Работа с правами =====
  
    // 1. Создать новое право
    async createPermission(permissionData) {
      this.loadingPermissions = true;
      try {
        const { data } = await api.post('/roles/permissions', permissionData);
        runInAction(() => {
          this.permissions.push(data);
          this.error = null;
        });
        return data;
      } catch (err) {
        runInAction(() => {
          this.error = err.message || 'Ошибка при создании права';
        });
        throw err;
      } finally {
        runInAction(() => {
          this.loadingPermissions = false;
        });
      }
    }
  
    // 2. Обновить существующее право
    async updatePermission(permissionId, permissionData) {
      this.loadingPermissions = true;
      try {
        const { data } = await api.patch(
          `/roles/permissions/${permissionId}`,
          permissionData
        );
        runInAction(() => {
          this.permissions = this.permissions.map(p =>
            p.id === permissionId ? data : p
          );
          this.error = null;
        });
        return data;
      } catch (err) {
        runInAction(() => {
          this.error = err.message || 'Ошибка при обновлении права';
        });
        throw err;
      } finally {
        runInAction(() => {
          this.loadingPermissions = false;
        });
      }
    }
  
    // 3. Удалить право
    async deletePermission(permissionId) {
      this.loadingPermissions = true;
      try {
        await api.delete(`/roles/permissions/${permissionId}`);
        runInAction(() => {
          this.permissions = this.permissions.filter(p => p.id !== permissionId);
          this.error = null;
        });
      } catch (err) {
        runInAction(() => {
          this.error = err.message || 'Ошибка при удалении права';
        });
        throw err;
      } finally {
        runInAction(() => {
          this.loadingPermissions = false;
        });
      }
    }
  
    // А метод загрузки прав должен выглядеть так:
    async fetchRolesAndPermissions() {
      this.loadingRoles = this.loadingPermissions = true;
      try {
        const { data } = await api.get('/roles/withAllPermissions');
        runInAction(() => {
          this.roles = data.roles;
          this.permissions = data.permissions;
          this.error = null;
        });
      } catch (err) {
        runInAction(() => {
          this.error = err.message || 'Ошибка при загрузке ролей и прав';
        });
      } finally {
        runInAction(() => {
          this.loadingRoles = this.loadingPermissions = false;
        });
      }
    }
}

export default AdminStore;
